import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { KeyRound, ClipboardCheck, Download, Loader2, Calendar, Wrench } from 'lucide-react';
import { tenantAPI } from '../../lib/api';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

const downloadPdf = async (url, filename) => {
  try {
    const token = localStorage.getItem('tenant_token');
    const r = await fetch(`${BACKEND}${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const blob = await r.blob();
    const dlUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dlUrl; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(dlUrl);
  } catch (e) {
    toast.error('Impossible de telecharger le document');
  }
};

export const TenantKeyHandovers = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tenantAPI.getKeyHandovers()
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" /></div>;
  if (list.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <KeyRound className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="font-medium">Aucune remise de cles enregistree</p>
          <p className="text-sm mt-1">Les remises de cles apparaitront ici apres signature avec votre agence.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tenant-key-handovers">
      {list.map((h) => (
        <Card key={h.id} className="hover:shadow-md transition">
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">
                  {h.handover_type === 'remise' || h.handover_type === 'entree' ? 'Remise de cles' : 'Restitution de cles'}
                </h3>
                <Badge variant="outline" className="text-[10px]">{h.reference}</Badge>
              </div>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> {formatDate(h.handover_date)}
              </p>
              {h.observations && (
                <p className="text-sm text-gray-700 mt-2 line-clamp-2">{h.observations}</p>
              )}
              <div className="mt-2 text-xs text-gray-500">
                {h.keys && h.keys.length > 0 && (
                  <span>{h.keys.reduce((acc, k) => acc + (k.quantity || 1), 0)} cle(s) remise(s)</span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadPdf(`/api/tenant/key-handovers/${h.id}/pdf`, `remise_cles_${h.reference}.pdf`)}
              data-testid={`download-handover-${h.id}`}
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const TenantInspections = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tenantAPI.getInspections()
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" /></div>;
  if (list.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <ClipboardCheck className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="font-medium">Aucun etat des lieux enregistre</p>
          <p className="text-sm mt-1">Les etats des lieux (entree et sortie) apparaitront ici apres validation par votre agence.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tenant-inspections">
      {list.map((i) => {
        const isEntry = i.inspection_type === 'entree' || i.inspection_type === 'entry';
        return (
          <Card key={i.id} className="hover:shadow-md transition">
            <CardContent className="p-4 flex items-start gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isEntry ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
              }`}>
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">
                    Etat des lieux {isEntry ? 'd\'entree' : 'de sortie'}
                  </h3>
                  {i.reference && <Badge variant="outline" className="text-[10px]">{i.reference}</Badge>}
                </div>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> {formatDate(i.inspection_date)}
                </p>
                {i.general_comment && (
                  <p className="text-sm text-gray-700 mt-2 line-clamp-2">{i.general_comment}</p>
                )}
                {Array.isArray(i.rooms) && i.rooms.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {i.rooms.length} piece(s) inspectee(s)
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadPdf(`/api/tenant/inspections/${i.id}/pdf`, `etat_lieux_${i.reference || i.id}.pdf`)}
                data-testid={`download-inspection-${i.id}`}
              >
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};


const STATUS_LABELS_REPAIR = {
  planifiee: { label: 'Planifiee', cls: 'bg-blue-100 text-blue-800' },
  en_cours: { label: 'En cours', cls: 'bg-amber-100 text-amber-800' },
  terminee: { label: 'Terminee', cls: 'bg-emerald-100 text-emerald-800' },
  annulee: { label: 'Annulee', cls: 'bg-gray-100 text-gray-700' },
};

export const TenantRepairs = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tenantAPI.getRepairs()
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" /></div>;
  if (list.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <Wrench className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="font-medium">Aucune reparation enregistree</p>
          <p className="text-sm mt-1">Les interventions sur votre logement apparaitront ici.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tenant-repairs">
      {list.map((r) => {
        const st = STATUS_LABELS_REPAIR[r.status] || STATUS_LABELS_REPAIR.planifiee;
        return (
          <Card key={r.id} className="hover:shadow-md transition">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#1B3A4B] text-[#D4A745] flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900 capitalize">{r.repair_type_label || r.repair_type}</h3>
                  <Badge variant="outline" className="text-[10px]">{r.reference}</Badge>
                  <Badge className={`${st.cls} text-[10px]`}>{st.label}</Badge>
                </div>
                <p className="text-sm text-gray-700 mt-1 line-clamp-2">{r.description}</p>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" />
                  {r.scheduled_date ? `Prevue ${formatDate(r.scheduled_date)}` : `Creee ${formatDate(r.created_at)}`}
                  {r.provider_name ? ` - Prestataire: ${r.provider_name}` : ''}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
